import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Divider from '@mui/material/Divider'
import Grid, { GridSize } from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { camelCase } from 'lodash'
import { FC, useEffect, useState } from 'react'

import { EventShirtDetailsProps } from 'constants/types'
import { Category, Event, Participant, ResponsiveStyleValue } from 'models/API'
import { isCategoryRelay, getShirtSize } from 'utils/ApiHelpers'

interface Props {
  participant: Participant
  event: Event
  onClick: React.MouseEventHandler<HTMLButtonElement>
}

const GridItem = ({
  label = '',
  value = '',
  size = { xs: 12, md: 6 },
}: {
  label: string
  value: string
  size?: ResponsiveStyleValue<GridSize>
}) => (
  <Grid size={size}>
    <Typography
      variant="overline"
      sx={{
        color: 'secondary.main',
      }}>
      {label}
    </Typography>
    <Typography>{(value ?? '')?.trim() !== '' ? value : '-'}</Typography>
  </Grid>
)

const SectionLabel = ({ label = '' }: { label: string }) => (
  <Typography
    variant="body2"
    sx={{
      textTransform: 'uppercase',
    }}>
    {label}
  </Typography>
)

const Details: FC<Props> = ({ participant, event, onClick }) => {
  const [category, setCategory] = useState<Category>()

  useEffect(() => {
    if (participant) {
      const selectedCategory = (event?.categories?.items?.filter(
        (cat) => cat.id === participant.categoryId,
      ) ?? []) as unknown as Category[]

      if (selectedCategory.length > 0) setCategory(selectedCategory[0])
    }
  }, [participant, event])

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <GridItem
          label="Category"
          value={
            event?.categories?.items?.filter(
              (cat) => cat.id === participant.categoryId,
            )[0]?.name
          }
        />

        {category && isCategoryRelay(category) && (
          <GridItem label="Relay Event" value={participant.relayEvent} />
        )}

        <GridItem
          label={camelCase(
            (event.shirtDetails as unknown as EventShirtDetailsProps)?.SHIRT1
              ?.LABEL || 'Shirt',
          )}
          value={getShirtSize(participant.shirtSize)}
        />

        {category?.hasShirt2 && (
          <GridItem
            label={camelCase(
              (event.shirtDetails as unknown as EventShirtDetailsProps)?.SHIRT2
                ?.LABEL || 'Shirt 2',
            )}
            value={getShirtSize(participant.shirtSize2)}
          />
        )}

        {category?.hasJersey && participant.jerseySize && (
          <GridItem
            label={camelCase(
              (event.shirtDetails as unknown as EventShirtDetailsProps)?.JERSEY
                ?.LABEL || 'Jersey',
            )}
            value={getShirtSize(participant.jerseySize)}
          />
        )}

        <GridItem label="Team Name" value={participant.teamName} />
        {participant.subCategory && (
          <GridItem label="Subcategory" value={participant.subCategory} />
        )}
      </Grid>

      <Divider />

      <SectionLabel label="Participant" />

      <Grid container spacing={2}>
        <GridItem label="First Name" value={participant.firstName} />
        <GridItem label="Last Name" value={participant.lastName} />

        <GridItem
          label="Birthdate"
          value={participant.birthdate}
          size={{ xs: 12, md: 4 }}
        />
        <GridItem
          label="Nationality"
          value={participant.nationality}
          size={{ xs: 12, md: 4 }}
        />
        <GridItem
          label="Gender"
          value={participant.gender}
          size={{ xs: 12, md: 4 }}
        />

        <GridItem
          label="Home Address"
          value={participant.address}
          size={{ xs: 12 }}
        />
        <GridItem label="Phone Number" value={participant.phoneNumber} />
      </Grid>

      {category?.isTandem && (
        <>
          <Divider />

          <SectionLabel label="Tandem Details" />

          <Grid container spacing={2}>
            <GridItem label="Name" value={participant.tandemName} />

            <GridItem label="Birthdate" value={participant.tandemBirthdate} />

            <GridItem
              label="Shirt"
              value={getShirtSize(participant.tandemShirtSize)}
            />
          </Grid>
        </>
      )}

      <Divider />

      <SectionLabel label="Emergency Contact Person" />

      <Grid container spacing={2}>
        <GridItem label="Name" value={participant.emergencyContactName} />

        <GridItem label="Phone" value={participant.emergencyContactPhone} />
      </Grid>
      <Box display="flex" flexDirection="column" alignItems="flex-end">
        <Button onClick={onClick}>Update</Button>
      </Box>
    </Stack>
  )
}

export default Details
